import React, { useState, useEffect } from 'react';
import api from '../api';
import { 
  LayoutDashboard, 
  Briefcase, 
  Users, 
  BarChart2, 
  Settings,
  LogOut,
  Clock,
  Zap,
  ChevronDown,
  CheckSquare,
  Bell,
  MessageSquareWarning,
  ClipboardList,
  CalendarOff,
  UtensilsCrossed,
  Clock3,
  RefreshCw,
  Target
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTimeTracking } from '../contexts/TimeTrackingContext';

const Navigation = ({ currentPage, onPageChange, unreadNotifCount = 0 }) => {
  const { user, token, logout, isAdmin, isPM, isTL, features } = useAuth();
  const { isWorking, currentJob, shiftStarted, isOnBreak, breakType } = useTimeTracking();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [priorityAccess, setPriorityAccess] = useState(true);
  const [cafeteriaAccess, setCafeteriaAccess] = useState(true);

  const MANAGER_ROLES = ['admin', 'pm', 'preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'production_lead', 'epson_lead'];

  // Normal users only see the Priority Projects tab if they have skill-matrix entries.
  useEffect(() => {
    if (!token || !user) return;
    if (MANAGER_ROLES.includes(user.role)) { setPriorityAccess(true); return; }
    let cancelled = false;
    api.get('/priority-projects/access', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { if (!cancelled) setPriorityAccess(!!res.data?.has_access); })
      .catch(() => { if (!cancelled) setPriorityAccess(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [token, user?.role]);

  // Hide the Cafeteria tab from users who have the cafeteria service disabled.
  useEffect(() => {
    if (!token || !user) return;
    let cancelled = false;
    api.get('/cafeteria/access', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { if (!cancelled) setCafeteriaAccess(res.data?.has_access !== false); })
      .catch(() => { if (!cancelled) setCafeteriaAccess(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [token, user?.role]);

  const getMenuItems = () => {
    const items = [
      { key: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard className="w-5 h-5" />, roles: ['preflight', 'preflight_lead', 'preflight_prepress', 'cmd', 'cmd_lead', 'prepress', 'prepress_lead', 'qc', 'qc_lead', 'production', 'production_lead', 'epson_lead', 'pm', 'admin'] },
      { key: 'jobs', label: 'Active Projects', icon: <Briefcase className="w-5 h-5" />, roles: ['preflight', 'preflight_lead', 'preflight_prepress', 'cmd', 'cmd_lead', 'prepress', 'prepress_lead', 'qc', 'qc_lead', 'production', 'production_lead', 'epson_lead', 'pm', 'admin'] },
      { key: 'priority-projects', label: 'Priority Projects', icon: <Zap className="w-5 h-5" />, roles: ['preflight', 'preflight_lead', 'preflight_prepress', 'cmd', 'cmd_lead', 'prepress', 'prepress_lead', 'qc', 'qc_lead', 'production', 'production_lead', 'epson_lead', 'pm', 'admin'] },
      { key: 'pending-jobs', label: 'Pending Jobs', icon: <Clock3 className="w-5 h-5" />, roles: ['preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'production_lead', 'epson_lead', 'pm', 'admin'] },
      { key: 'on-approvals', label: 'Project Status', icon: <CheckSquare className="w-5 h-5" />, roles: ['preflight', 'preflight_lead', 'preflight_prepress', 'cmd', 'cmd_lead', 'prepress', 'prepress_lead', 'qc', 'qc_lead', 'production', 'production_lead', 'epson_lead', 'pm', 'admin'] },
      { key: 'completed-projects', label: 'Completed Projects', icon: <Briefcase className="w-5 h-5" />, roles: ['preflight', 'preflight_lead', 'preflight_prepress', 'cmd', 'cmd_lead', 'prepress', 'prepress_lead', 'qc', 'qc_lead', 'production', 'production_lead', 'epson_lead', 'pm', 'admin'] },
      { key: 'repeat-orders', label: 'Repeat Orders', icon: <RefreshCw className="w-5 h-5" />, roles: ['preflight', 'preflight_lead', 'preflight_prepress', 'cmd', 'cmd_lead', 'prepress', 'prepress_lead', 'qc', 'qc_lead', 'production', 'production_lead', 'epson_lead', 'pm', 'admin'] },
      { key: 'notifications', label: 'Notifications', icon: <Bell className="w-5 h-5" />, roles: ['preflight', 'preflight_lead', 'preflight_prepress', 'cmd', 'cmd_lead', 'prepress', 'prepress_lead', 'qc', 'qc_lead', 'production', 'production_lead', 'epson_lead', 'pm', 'admin'] },
      { key: 'reports', label: 'Reports', icon: <BarChart2 className="w-5 h-5" />, roles: ['preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'production_lead', 'epson_lead', 'pm', 'admin'] },
      { key: 'pointing-system', label: 'Pointing System', icon: <Target className="w-5 h-5" />, roles: ['preflight', 'preflight_lead', 'preflight_prepress', 'cmd', 'cmd_lead', 'prepress', 'prepress_lead', 'qc', 'qc_lead', 'production', 'production_lead', 'epson_lead', 'pm', 'admin'] },
      { key: 'shifts', label: 'Shifts', icon: <Clock className="w-5 h-5" />, roles: ['preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'production_lead', 'epson_lead', 'pm', 'admin'] },
      // Operators are included so they can read the CAPAs that name them; the
      // backend scopes their list to those and hides everyone else's.
      { key: 'capa', label: 'CAPA', icon: <ClipboardList className="w-5 h-5" />, roles: ['preflight', 'preflight_lead', 'preflight_prepress', 'cmd', 'cmd_lead', 'cmd_qc', 'prepress', 'prepress_lead', 'qc', 'qc_lead', 'production', 'production_lead', 'epson', 'epson_lead', 'pm', 'admin'] },
      { key: 'leaves', label: 'Leaves', icon: <CalendarOff className="w-5 h-5" />, roles: ['preflight', 'preflight_lead', 'preflight_prepress', 'cmd', 'cmd_lead', 'prepress', 'prepress_lead', 'qc', 'qc_lead', 'production', 'production_lead', 'epson_lead', 'pm', 'admin'] },
      { key: 'clock-in', label: 'Clock In', icon: <Clock className="w-5 h-5" />, roles: ['accounts', 'support', 'it', 'receptionist'] },
      { key: 'cafeteria', label: 'Cafeteria', icon: <UtensilsCrossed className="w-5 h-5" />, roles: ['preflight', 'preflight_lead', 'preflight_prepress', 'cmd', 'cmd_lead', 'prepress', 'prepress_lead', 'qc', 'qc_lead', 'production', 'production_lead', 'epson_lead', 'pm', 'admin', 'cafeteria', 'receptionist', 'accounts', 'support', 'it', 'dispatch'] },
      { key: 'timesheet', label: 'Time Sheet', icon: <Clock className="w-5 h-5" />, roles: ['preflight', 'preflight_lead', 'preflight_prepress', 'cmd', 'cmd_lead', 'prepress', 'prepress_lead', 'qc', 'qc_lead', 'production', 'production_lead', 'epson_lead', 'pm', 'admin', 'accounts', 'support', 'it', 'receptionist'] },
      { key: 'important-messages', label: 'Alert Messages', icon: <MessageSquareWarning className="w-5 h-5" />, roles: ['admin', 'pm', 'preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'production_lead', 'epson_lead'] },
      { key: 'audit-trails', label: 'Audit Trails', icon: <ClipboardList className="w-5 h-5" />, roles: ['admin', 'pm'] },
    ];

    // if (!isAdmin) {
    //   items.push(
    //     { key: 'jobs', label: 'My Jobs', icon: <Briefcase className="w-5 h-5" />, roles: ['preflight', 'preflight_lead', 'cmd', 'cmd_lead', 'prepress', 'prepress_lead', 'qc', 'qc_lead', 'pm', 'admin'] }
    //   );
    // }
    
    // items.push(
    //   { key: 'jobs', label: 'My Jobs', icon: <Briefcase className="w-5 h-5" />, roles: ['preflight', 'preflight_lead', 'cmd', 'cmd_lead', 'prepress', 'prepress_lead', 'qc', 'qc_lead', 'pm', 'admin'] },
    //   { key: 'reports', label: 'Reports', icon: <BarChart2 className="w-5 h-5" />, roles: ['preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'pm', 'admin'] },
    //   { key: 'timesheet', label: 'Time Sheet', icon: <Clock className="w-5 h-5" />, roles: ['preflight', 'preflight_lead', 'cmd', 'cmd_lead', 'prepress', 'prepress_lead', 'qc', 'qc_lead', 'pm', 'admin'] }
    // );

    if (isAdmin) {
      items.push(
        { key: 'admin', label: 'Settings', icon: <Settings className="w-4 h-4" />, roles: ['admin'] },
         { key: 'docs', label: 'Doc', icon: <Users className="w-4 h-4" />, roles: ['admin'] },
      );
    }

    return items.filter(item => {
      if (!item.roles.includes(user.role)) return false;
      // Hide Priority Projects from non-managers who have no skill-matrix entries.
      if (item.key === 'priority-projects' && !MANAGER_ROLES.includes(user.role) && !priorityAccess) {
        return false;
      }
      // Hide Cafeteria if the service is disabled for this user.
      if (item.key === 'cafeteria' && !cafeteriaAccess) {
        return false;
      }
      // Repeat Orders is available to everyone unless an Admin has switched it
      // off for this user in Settings → User Management.
      if (item.key === 'repeat-orders' && features?.repeat_orders === false) {
        return false;
      }
      return true;
    });
  };

  const getStatusChips = () => {
    const chips = [];
    
    if (shiftStarted) {
      chips.push(
        <div key="shift" className="w-full px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium text-center">
          Shift Active
        </div>
      );
    }
    
    if (isWorking && currentJob) {
      chips.push(
        <div key="working" className="w-full px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium text-center">
          Working: Job {currentJob}
        </div>
      );
    }
    
    if (isOnBreak) {
      chips.push(
        <div key="break" className="w-full px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium text-center">
          {breakType} Break
        </div>
      );
    }
    
    return chips;
  };

  return (
    <aside className="fixed top-0 left-0 w-72 h-screen bg-white border-r border-gray-100 flex flex-col z-50"  style={{ backgroundColor: '#01158a', color: 'white' }}>
      {/* User Profile Section */}
      <div className="p-6 flex items-center gap-3">
        {/* <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center text-white font-bold">
          <Zap className="w-5 h-5" />
        </div> */}
        <div className="overflow-hidden" >
          {/* <h2 className="text-sm font-bold text-gray-900 truncate">Freelancer Space</h2>
          <p className="text-xs text-gray-500 truncate">Individuals</p> */}
          {/* need to add logo here from images/Logo-01.png */}
          <img src="/images/Logo-01.png" alt="Logo" className="w-44 h-auto" />
        </div>
        {/* <div className="ml-auto text-gray-400">
           <ChevronDown className="w-4 h-4" />
        </div> */}
      </div>
      
      {/* <div className="px-6 mt-3 mb-2">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">GENERAL</p>
      </div> */}

      <div className="flex-1 overflow-y-auto px-4 mt-3">
        <nav className="space-y-1">
          {getMenuItems().map((item) => (
            <button
              key={item.key}
              onClick={() => onPageChange(item.key)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-colors duration-200 ${
                currentPage === item.key
                  ? 'bg-cyan-50 text-cyan-600'
                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <span className={`${currentPage === item.key ? 'text-cyan-600' : 'text-gray-400'}`}>
                {item.icon}
              </span>
              <span className="flex-1 text-left">{item.label}</span>
              {item.key === 'notifications' && unreadNotifCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-bold bg-red-500 text-white">
                  {unreadNotifCount > 99 ? '99+' : unreadNotifCount}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-4 border-t border-gray-100">
         <div className="flex flex-col gap-2 mb-4">
            {getStatusChips()}
         </div>

         <div className="relative">
            <button 
               onClick={() => setShowUserMenu(!showUserMenu)}
               className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-gray-300 hover:bg-white/10 hover:text-white rounded-xl transition-colors duration-200"
            >
               {/* <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-bold">
                  {user?.name?.charAt(0) || 'U'}
               </div> */}
               <div className="flex-1 text-left overflow-hidden">
                  <p className="text-sm font-medium text-white truncate">{user?.name || 'User'}</p>
                  <p className="text-xs text-gray-400 truncate capitalize">{user?.role || 'Role'}</p>
               </div>
               <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
            </button>

            {showUserMenu && (
               <div className="absolute bottom-full left-0 w-full mb-2 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden p-2 z-50">
                  <div className="px-4 py-3 border-b border-gray-100 mb-2">
                     <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                     <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>
                  
                  {/* <button className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors">
                     <Settings className="w-4 h-4" />
                     Settings
                  </button> */}
                  
                  <button 
                     onClick={logout}
                     className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                     <LogOut className="w-4 h-4" />
                     Logout
                  </button>
               </div>
            )}
         </div>
      </div>
    </aside>
  );
};

export default Navigation;
